/**
 * Job Description — Lane 1 (deterministic cleanup, NO AI).
 *
 * Translates raw federal-solicitation fields into clean, candidate-facing copy
 * BEFORE the constrained AI translation (Lane 2), and sanitizes the AI output
 * afterward as a hard guard. No model calls — pure, testable string work.
 *
 *  - normalizeAgency:   "VETERANS AFFAIRS, DEPARTMENT OF" → "U.S. Department of Veterans Affairs (VA)"
 *  - stripCandidateRefs: remove solicitation/internal IDs, PWS/CLIN/task/FAR refs
 *  - expandAcronyms:    expand known acronyms on first use; never guess unknowns
 *  - dedupe/contingent: each fact appears once (the banner owns "contingent on award")
 */

import { cleanTitle, decodeHtmlEntities } from './textCleanup';
import type { JobDescription, JobDescriptionInput } from '../../types/job-description';

// Default "About Us" used when the company profile hasn't set one. Editable in
// Company Profile > Company Information; pulled VERBATIM into postings (never AI).
export const SEED_ABOUT_US =
  'SerVet Digital, LLC is a Service-Disabled Veteran-Owned Small Business delivering ' +
  'federal training and digital solutions. We pair awarded course-development past ' +
  'performance with modern, accessible, AI-accelerated e-learning and custom software ' +
  'for government and enterprise clients.';

// ---------------------------------------------------------------------------
// Agencies — canonical, readable names
// ---------------------------------------------------------------------------
const AGENCY_MAP: Array<{ test: RegExp; name: string }> = [
  { test: /veterans? affairs/i, name: 'U.S. Department of Veterans Affairs (VA)' },
  { test: /\bdefense\b|department of defense|\bdod\b/i, name: 'U.S. Department of Defense (DoD)' },
  { test: /homeland security/i, name: 'U.S. Department of Homeland Security (DHS)' },
  { test: /health and human services/i, name: 'U.S. Department of Health and Human Services (HHS)' },
  { test: /\bagriculture\b/i, name: 'U.S. Department of Agriculture (USDA)' },
  { test: /\bjustice\b/i, name: 'U.S. Department of Justice (DOJ)' },
  { test: /\btreasury\b/i, name: 'U.S. Department of the Treasury' },
  { test: /\benergy\b/i, name: 'U.S. Department of Energy (DOE)' },
  { test: /\beducation\b/i, name: 'U.S. Department of Education' },
  { test: /\binterior\b/i, name: 'U.S. Department of the Interior (DOI)' },
  { test: /\bstate\b, department/i, name: 'U.S. Department of State' },
  { test: /\blabor\b/i, name: 'U.S. Department of Labor (DOL)' },
  { test: /\btransportation\b/i, name: 'U.S. Department of Transportation (DOT)' },
  { test: /\bcommerce\b/i, name: 'U.S. Department of Commerce (DOC)' },
  { test: /general services administration|\bgsa\b/i, name: 'U.S. General Services Administration (GSA)' },
  { test: /\barmy\b/i, name: 'U.S. Army' },
  { test: /\bnavy\b/i, name: 'U.S. Navy' },
  { test: /air force/i, name: 'U.S. Air Force' },
];

function titleCase(s: string): string {
  return s.toLowerCase().replace(/\b([a-z])/g, (m) => m.toUpperCase());
}

export function normalizeAgency(raw: string): string {
  const a = decodeHtmlEntities(raw || '').replace(/\s+/g, ' ').trim();
  if (!a) return '';
  for (const { test, name } of AGENCY_MAP) if (test.test(a)) return name;
  // Generic SAM "X, DEPARTMENT OF" → "U.S. Department of X"
  const m = a.match(/^(.*),\s*department of\.?$/i);
  if (m) return `U.S. Department of ${titleCase(m[1].trim())}`;
  // Otherwise just make it presentable.
  return a === a.toUpperCase() ? titleCase(a) : a;
}

// ---------------------------------------------------------------------------
// Acronyms — expand on first use; never guess
// ---------------------------------------------------------------------------
const ACRONYMS: Record<string, string> = {
  COR: "Contracting Officer's Representative",
  CLIN: 'Contract Line Item Number',
  IDIQ: 'Indefinite Delivery/Indefinite Quantity',
  'FAC-P/PM': 'Federal Acquisition Certification for Program/Project Managers',
  PMP: 'Project Management Professional',
  RWD: 'Responsive Web Design',
  SME: 'Subject Matter Expert',
  TMS: 'Talent Management System',
};
// Acronyms common enough to leave as-is for a candidate audience.
const ACRONYM_ALLOWLIST = new Set<string>(['HTML5', 'AA', 'IT', 'QA', 'US', 'U.S.', 'PWS', 'SOW', 'CLIN']);

export function expandAcronymsFirstUse(text: string): string {
  if (!text) return text;
  let out = text;
  for (const [acr, full] of Object.entries(ACRONYMS)) {
    if (ACRONYM_ALLOWLIST.has(acr)) continue;
    if (out.includes(`(${acr})`) || out.includes(full)) continue; // already expanded
    const esc = acr.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&');
    // Boundary on alphanumerics only, so end-of-sentence acronyms ("…TMS.") expand.
    const re = new RegExp(`(?<![A-Za-z0-9])${esc}(?![A-Za-z0-9])`);
    if (re.test(out)) out = out.replace(re, `${full} (${acr})`);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Strip solicitation/internal references from candidate-facing copy
// ---------------------------------------------------------------------------
function tidy(text: string): string {
  return text
    .replace(/\(\s*[—–\-·,;:]*\s*\)/g, '')                         // empty / punctuation-only parens
    .replace(/\[\s*\]/g, '')
    // orphan connectors left behind after stripping a reference ("…per and CLIN" → "…")
    .replace(/\b(per|across|under|via|pursuant to|in accordance with)\b(?:\s+(?:and|the|a|an))?\s*(?=[.,;:)]|$)/gi, '')
    .replace(/\b(and|per)\s+(and|per)\b/gi, '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\s+([.,;:)])/g, '$1')
    .replace(/\(\s+/g, '(')
    .replace(/^\s*[:：]\s*/, '')                                   // leading dangling colon
    .replace(/\s+-\s+(?=$|[.,;])/g, '')
    .replace(/(^|\s)[-–—|:]\s*(?=$)/g, '')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

export function stripCandidateRefs(text: string, opts?: { solicitationNumber?: string }): string {
  if (!text) return '';
  let t = decodeHtmlEntities(text);
  if (opts?.solicitationNumber) t = t.split(opts.solicitationNumber).join('');
  // Translate a leading "Task N (PWS x.x): <label>" into just "<label>" (keep the
  // plain label, drop the number + colon) rather than leaving a dangling colon.
  t = t.replace(/^\s*Task\s+\d+[^:]{0,60}:\s*/i, '');
  t = t
    .replace(/\bP\.?W\.?S\.?\s*§?\s*\d+(?:\.\d+)*/gi, '')           // PWS 7.3 / P.W.S. §7.3
    .replace(/\bS\.?O\.?W\.?\s*§?\s*\d+(?:\.\d+)*/gi, '')            // SOW 3.1
    .replace(/\bCLIN[s]?\s*\d{2,}(?:\s*[-–]\s*\d{2,})?/gi, '')       // CLIN 0001-0005
    .replace(/\bTask\s+(?:Order\s+)?\d+(?:\.\d+)*/gi, '')           // Task 3 / Task 7.2
    .replace(/\bAttachment\s+[A-Z]\b/gi, '')                        // Attachment B
    .replace(/\bFAR\s+52\.\d{3}(?:-\d+)?/gi, '')                    // FAR 52.219-27
    .replace(/\bDFARS\s+252\.\d{3}(?:-\d+)?/gi, '')                 // DFARS 252.xxx
    .replace(/§\s*\d+(?:\.\d+)+/g, '')                              // §7.3
    .replace(/\b[A-Z]{1,5}\d{0,4}--FY\d{2,4}\b/g, '')              // U008--FY26
    .replace(/\bRF[QPI]\s+[A-Z0-9-]{6,}/gi, '')                     // RFQ 36C10X26Q0124
    .replace(/\b[0-9][0-9A-Z]{9,}\b/g, '')                          // bare solicitation IDs
    .replace(/\bamendment\s+\d+\b/gi, '');                          // "Amendment 1"
  return tidy(t);
}

// Blank out meta placeholders the model sometimes emits ("placeholder — …",
// "not specified in source") so they never reach the candidate; the field then
// routes to the human (editable, omitted from exports when empty).
export function stripMetaPlaceholder(s: string): string {
  if (!s) return '';
  if (/^\s*placeholder\b/i.test(s) || /\breplaced verbatim\b/i.test(s) || /\bnot (specified|provided)\b.*\bsource\b/i.test(s)) return '';
  return s;
}

// Remove "contingent upon award" sentences from prose (the banner states it once).
export function removeContingentSentences(text: string): string {
  if (!text) return text;
  return tidy(
    text
      .split(/(?<=[.!?])\s+/)
      .filter((s) => !/contingent\b.{0,20}\baward/i.test(s))
      .join(' ')
  );
}

function cleanLine(line: string, sol?: string): string {
  return tidy(expandAcronymsFirstUse(stripCandidateRefs(line, { solicitationNumber: sol })));
}

function cleanList(list: string[] | undefined, sol?: string): string[] {
  const seen = new Set<string>();
  return (list || [])
    .map((l) => cleanLine(l, sol))
    .filter(Boolean)
    .filter((l) => {
      const k = l.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
}

// ---------------------------------------------------------------------------
// Duty vs. Program-Scope routing + filler suppression
// ---------------------------------------------------------------------------
// Role-action bullets ("Lead…", "Produce…") are what the PERSON does →
// Key Responsibilities. Noun-phrase task names are the contract's → Program Scope.
const ACTION_VERB = /^(lead|manage|produce|review|collaborate|develop|design|build|deliver|coordinate|conduct|prepare|maintain|support|implement|test|document|analyze|author|configure|integrate|ensure|oversee|plan|facilitate|train|validate|create|write|provide|execute|monitor|assess|evaluate|drive|own|serve|administer|perform)\b/i;
// Tautological filler that should route to the human, never ship.
const FILLER = /^(perform the duties of\b|collaborate with the project team and the government|support the program\.?$|responsible for (the )?role\b|perform duties in accordance)/i;

export function reclassifyDutiesAndScope(
  responsibilities: string[],
  scope: string[]
): { keyResponsibilities: string[]; programScope: string[] } {
  const seen = new Set<string>();
  const all = [...(responsibilities || []), ...(scope || [])]
    .map((s) => (s || '').trim())
    .filter(Boolean)
    .filter((b) => !FILLER.test(b))
    .filter((b) => {
      const k = b.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  return {
    keyResponsibilities: all.filter((b) => ACTION_VERB.test(b)),
    programScope: all.filter((b) => !ACTION_VERB.test(b)),
  };
}

// Acronyms we never flag (known/expandable or common-knowledge to candidates).
const ACRONYM_KNOWN = new Set<string>([
  ...Object.keys(ACRONYMS), ...ACRONYM_ALLOWLIST,
  'EEO', 'USA', 'US', 'VA', 'DOD', 'DHS', 'HHS', 'SDVOSB', 'VOSB', 'WOSB', 'HUBZONE', 'SBA',
  'LMS', 'UI', 'UX', 'API', 'AI', 'PDF', 'SCORM', 'WCAG', 'ADDIE',
]);

// Unknown acronyms left in candidate copy (never guessed) — surfaced to the human.
export function detectUnknownAcronyms(jd: JobDescription): string[] {
  const fields: string[] = [
    jd.jobTitle, jd.positionSummary,
    ...(jd.keyResponsibilities || []), ...(jd.programScope || []),
    ...(jd.requiredQualifications?.requiredSkills || []),
    ...(jd.preferredQualifications || []),
    jd.requiredQualifications?.education || '', jd.requiredQualifications?.yearsExperience || '',
  ];
  const found = new Set<string>();
  for (const text of fields) {
    for (const m of (text || '').matchAll(/\b[A-Z][A-Z0-9]{1,6}\b/g)) {
      if (!ACRONYM_KNOWN.has(m[0])) found.add(m[0]);
    }
  }
  return Array.from(found);
}

// ---------------------------------------------------------------------------
// Lane 1a — normalize the INPUT fields before the AI translation
// ---------------------------------------------------------------------------
export function normalizeJobInput(input: JobDescriptionInput): JobDescriptionInput {
  const sol = input.solicitation?.solicitationNumber;
  return {
    ...input,
    role: {
      ...input.role,
      positionTitle: cleanTitle(stripCandidateRefs(input.role.positionTitle, { solicitationNumber: sol })) || input.role.positionTitle,
      minimumQualifications: cleanList(input.role.minimumQualifications, sol),
    },
    solicitation: {
      ...input.solicitation,
      agency: normalizeAgency(input.solicitation.agency),
      title: cleanTitle(stripCandidateRefs(input.solicitation.title, { solicitationNumber: sol })),
      scopeSummary: input.solicitation.scopeSummary
        ? cleanLine(input.solicitation.scopeSummary, sol)
        : input.solicitation.scopeSummary,
      relevantTasks: cleanList(input.solicitation.relevantTasks, sol),
    },
  };
}

// ---------------------------------------------------------------------------
// Lane 1b — sanitize the AI OUTPUT (hard guard: no refs/dupes leak)
// ---------------------------------------------------------------------------
export function sanitizeJobDescriptionOutput(jd: JobDescription, solicitationNumber?: string): JobDescription {
  const sol = solicitationNumber || jd.sourceTrace?.solicitationNumber;
  const line = (s: string) => cleanLine(s || '', sol);

  // Clean both duty lists, then route action bullets → Responsibilities and task
  // names → Program Scope (drops filler/tautologies in the process).
  const routed = reclassifyDutiesAndScope(
    cleanList(jd.keyResponsibilities, sol),
    cleanList(jd.programScope, sol)
  );

  return {
    ...jd,
    jobTitle: cleanTitle(
      stripCandidateRefs(jd.jobTitle, { solicitationNumber: sol }).replace(/\s*[\(\[][^)\]]*[\)\]]\s*$/, '')
    ) || jd.jobTitle,
    companyOverview: line(jd.companyOverview),
    // Contingent statement lives ONLY in the banner; strip from prose.
    positionSummary: removeContingentSentences(line(jd.positionSummary)),
    keyResponsibilities: routed.keyResponsibilities,
    programScope: routed.programScope,
    requiredQualifications: {
      ...jd.requiredQualifications,
      education: line(jd.requiredQualifications?.education || ''),
      yearsExperience: line(jd.requiredQualifications?.yearsExperience || ''),
      requiredCertifications: cleanList(jd.requiredQualifications?.requiredCertifications, sol),
      requiredSkills: cleanList(jd.requiredQualifications?.requiredSkills, sol),
      clearanceOrBackground: jd.requiredQualifications?.clearanceOrBackground || '', // from source/Lane 3 — leave verbatim
    },
    preferredQualifications: cleanList(jd.preferredQualifications, sol),
    travel: stripMetaPlaceholder(line(jd.travel)),
    eeoStatement: jd.eeoStatement, // boilerplate — leave intact
    howToApply: removeContingentSentences(line(jd.howToApply)),
  };
}
